import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X, Plus } from "lucide-react";
import { translations, cuisineTypes } from "@/lib/translations";

interface CuisinePreferencesProps {
  selectedCuisines?: string[];
  includedIngredients?: string[];
  excludedIngredients?: string[];
  onCuisineChange?: (cuisines: string[]) => void;
  onIncludeIngredient?: (ingredient: string) => void;
  onExcludeIngredient?: (ingredient: string) => void;
  onRemoveIncludedIngredient?: (ingredient: string) => void;
  onRemoveExcludedIngredient?: (ingredient: string) => void;
}

const defaultCuisines = Object.keys(cuisineTypes);

const CuisinePreferences = ({
  selectedCuisines = ["Italian", "Japanese"],
  includedIngredients = ["טחינה", "חומוס", "לימון"],
  excludedIngredients = [],
  onCuisineChange = () => {},
  onIncludeIngredient = () => {},
  onExcludeIngredient = () => {},
  onRemoveIncludedIngredient = () => {},
  onRemoveExcludedIngredient = () => {},
}: CuisinePreferencesProps) => {
  const [includeInput, setIncludeInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");

  const handleIncludeIngredient = () => {
    if (includeInput.trim()) {
      onIncludeIngredient(includeInput.trim());
      setIncludeInput("");
    }
  };

  const handleExcludeIngredient = () => {
    if (excludeInput.trim()) {
      onExcludeIngredient(excludeInput.trim());
      setExcludeInput("");
    }
  };

  return (
    <div className="w-full max-w-[800px] space-y-6 bg-white p-4 md:p-6 rounded-lg">
      <div className="space-y-4">
        <h3 className="text-xl md:text-2xl font-semibold text-right">
          {translations.mealPlanner.cuisine.title}
        </h3>

        {/* Cuisine Selection */}
        <Card className="p-4 space-y-4">
          <h4 className="text-base md:text-lg font-medium text-right">
            {translations.mealPlanner.cuisine.selectTypes}
          </h4>
          <div className="flex flex-wrap gap-2">
            {defaultCuisines.map((cuisine) => (
              <Button
                key={cuisine}
                variant={
                  selectedCuisines.includes(cuisine) ? "default" : "outline"
                }
                size="sm"
                className="text-sm"
                onClick={() => {
                  const newSelection = selectedCuisines.includes(cuisine)
                    ? selectedCuisines.filter((c) => c !== cuisine)
                    : [...selectedCuisines, cuisine];
                  onCuisineChange(newSelection);
                }}
              >
                {cuisineTypes[cuisine as keyof typeof cuisineTypes]}
              </Button>
            ))}
          </div>
        </Card>

        {/* Ingredient Preferences */}
        <Card className="p-4 space-y-4">
          <h4 className="text-base md:text-lg font-medium text-right">
            {translations.mealPlanner.cuisine.ingredientPreferences}
          </h4>

          {/* Include Ingredients */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-right block">
              {translations.mealPlanner.cuisine.includeIngredients}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground rtl-icon" />
                <Input
                  value={includeInput}
                  onChange={(e) => setIncludeInput(e.target.value)}
                  placeholder={translations.mealPlanner.cuisine.searchInclude}
                  className="rtl-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleIncludeIngredient();
                    }
                  }}
                />
              </div>
              <Button
                size="icon"
                variant="outline"
                onClick={handleIncludeIngredient}
                type="button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {includedIngredients.map((ingredient, index) => (
                <Badge key={`${ingredient}-${index}`} variant="secondary">
                  {ingredient}
                  <X
                    className="mr-2 h-3 w-3 cursor-pointer"
                    onClick={() => onRemoveIncludedIngredient(ingredient)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          {/* Exclude Ingredients */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-right block">
              {translations.mealPlanner.cuisine.excludeIngredients}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground rtl-icon" />
                <Input
                  value={excludeInput}
                  onChange={(e) => setExcludeInput(e.target.value)}
                  placeholder={translations.mealPlanner.cuisine.searchExclude}
                  className="rtl-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleExcludeIngredient();
                    }
                  }}
                />
              </div>
              <Button
                size="icon"
                variant="outline"
                onClick={handleExcludeIngredient}
                type="button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {excludedIngredients.map((ingredient, index) => (
                <Badge key={`${ingredient}-${index}`} variant="destructive">
                  {ingredient}
                  <X
                    className="mr-2 h-3 w-3 cursor-pointer"
                    onClick={() => onRemoveExcludedIngredient(ingredient)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CuisinePreferences;
